import { FunctionComponent, useEffect, useCallback, useContext } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    trackingConstants,
} from 'dibs-tracking';
import { useCheckoutSelector, useAppDispatch } from './reducers/root';
import { getCurrentPane } from './reducers/checkoutPaneReducer';
import { usePrerenderedCheckoutStore } from './prerender/prerenderedCheckoutStoreReact';
import { useBeforeUnload } from './shared/hooks/useBeforeUnload';
import { trackLabel } from './helpers/checkoutTracking';
import { setUserMismatchErrorAction } from './actions/commonActions';
import { setTriggeredErrors } from './actions/checkoutUIActions';
import { useFDFQuoteVariant } from './hooks/useFDFQuoteVariant';

import { CheckoutMobileSummaryNewPane } from './components/summary/CheckoutMobileSummaryNewPane';
import CheckoutSidebar from './sidebar/CheckoutSidebar';
import CheckoutMainError from './shared/CheckoutMainError';
import { CheckoutFooter } from './footer/CheckoutFooter';
import { CheckoutFrame } from './components/containers/CheckoutFrame';
import { CheckoutSteps } from './CheckoutSteps';
import CheckoutHelp from './components/checkoutHelp/CheckoutHelp';
import { PreloadedDuties } from './delivery/usePreloadedDuties';
import { CheckoutStepsSuspense } from './CheckoutStepsSuspense';
import { CheckoutMobileCancelationPolicy } from './components/shared/CheckoutMobileCancelationPolicy';
import { isCheckoutCancelationPolicyVariant } from './helpers/checkoutCancelationPolicyTestHelpers';
import { CheckoutServerVarsContext } from './components/shared/CheckoutServerVarsContext';
import { isEnhancedBuyerIncentivesVariant } from './helpers/isEnhancedBuyerIncentivesVariant';

import { CheckoutLayout_viewer$key } from './__generated__/CheckoutLayout_viewer.graphql';
import { CheckoutLayout_item$key } from './__generated__/CheckoutLayout_item.graphql';
import { CheckoutLayout_buyer$key } from './__generated__/CheckoutLayout_buyer.graphql';
import { CheckoutLayout_transaction$key } from './__generated__/CheckoutLayout_transaction.graphql';
import { CheckoutLayout_preCheckoutTransaction$key } from './__generated__/CheckoutLayout_preCheckoutTransaction.graphql';
import { CheckoutLayout_deliveryOptions$key } from './__generated__/CheckoutLayout_deliveryOptions.graphql';

import styles from './styles/CheckoutLayout.scss';

const viewerFragment = graphql`
    fragment CheckoutLayout_viewer on Viewer {
        ...CheckoutMobileSummaryNewPane_viewer
        ...CheckoutSidebar_viewer
        ...CheckoutFooter_viewer
        ...CheckoutSteps_viewer
        ...CheckoutHelp_viewer
    }
`;

const itemFragment = graphql`
    fragment CheckoutLayout_item on Item {
        ...useFDFQuoteVariant_item
        ...CheckoutMobileSummaryNewPane_item
        ...CheckoutSidebar_item
        ...CheckoutFrame_item
        ...CheckoutSteps_item
        ...CheckoutMobileCancelationPolicy_item
    }
`;

const buyerFragment = graphql`
    fragment CheckoutLayout_buyer on User {
        ...useFDFQuoteVariant_buyer
        ...CheckoutSidebar_buyer
        ...CheckoutSteps_buyer
        ...CheckoutHelp_buyer
        ...CheckoutMobileSummaryNewPane_buyer
    }
`;

const transactionFragment = graphql`
    fragment CheckoutLayout_transaction on Transaction {
        ...CheckoutMobileSummaryNewPane_transaction
        ...CheckoutSidebar_transaction
        ...CheckoutFooter_transaction
        ...CheckoutSteps_transaction
    }
`;

const preCheckoutTransactionFragment = graphql`
    fragment CheckoutLayout_preCheckoutTransaction on Transaction {
        ...CheckoutSteps_preCheckoutTransaction
    }
`;

const deliveryOptionsFragment = graphql`
    fragment CheckoutLayout_deliveryOptions on ShipmentQuote @relay(plural: true) {
        ...CheckoutSteps_deliveryOptions
        ...useFDFQuoteVariant_deliveryOptions
    }
`;

export type CheckoutLayoutProps = {
    inlineSummary: boolean;
    viewer: CheckoutLayout_viewer$key | null;
    item: CheckoutLayout_item$key | null;
    buyer: CheckoutLayout_buyer$key | null;
    transaction: CheckoutLayout_transaction$key | null;
    preCheckoutTransaction: CheckoutLayout_preCheckoutTransaction$key | null;
    deliveryOptions: CheckoutLayout_deliveryOptions$key | null;
    isComputing: boolean;
    preloadedDuties: PreloadedDuties;
};

export const CheckoutLayout: FunctionComponent<CheckoutLayoutProps> = props => {
    const { inlineSummary, isComputing, preloadedDuties } = props;
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const dispatch = useAppDispatch();

    const isEnhancedBuyerIncentivesClientVariant = isEnhancedBuyerIncentivesVariant();
    const hasBanner = !!useCheckoutSelector(state => state.ui.banner);
    const allowExitCheckout = useCheckoutSelector(state => state.ui.allowExitCheckout);
    const userMismatch = useCheckoutSelector(state => state.transaction.userMismatch);
    const paneState = useCheckoutSelector(state => state.pane);
    const wrapperClasses = classnames(styles.wrapper, {
        [styles.bannerAdjusted]: hasBanner,
        [styles.isEnhancedBuyerIncentivesClientVariant]:
            isMobile && isEnhancedBuyerIncentivesClientVariant,
    });

    const viewer = useFragment(viewerFragment, props.viewer);
    const item = useFragment(itemFragment, props.item);
    const buyer = useFragment(buyerFragment, props.buyer);
    const transaction = useFragment(transactionFragment, props.transaction);
    const preCheckoutTransaction = useFragment(
        preCheckoutTransactionFragment,
        props.preCheckoutTransaction
    );
    const deliveryOptions = useFragment(deliveryOptionsFragment, props.deliveryOptions);

    const { rootState } = usePrerenderedCheckoutStore();

    const trackOpenBrowserModal = useCallback(() => {
        trackEvent(
            {
                category: 'abandoned checkout',
                action: 'abandon browser modal display',
                label: trackLabel(getCurrentPane(paneState)),
                isInteractiveEvent: false,
                eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
                interaction_type: interactionTypeConstants.INTERACTION_TYPE_CHECKOUT_ABANDONED,
                step_interaction_name:
                    stepInteractionConstants.STEP_INTERACTION_ABANDON_CHECKOUT_BROWSER_MODAL,
            },
            null
        );
    }, [paneState]);

    useBeforeUnload({
        isActive: !allowExitCheckout && !inlineSummary && rootState === 'NOT_INITIALIZED',
        trackOpen: trackOpenBrowserModal,
    });

    useEffect(() => {
        if (userMismatch) {
            trackEvent(
                {
                    category: trackingConstants.ECOM_PRODUCT_CHECKOUT,
                    action: 'deadend error',
                    label: 'userMismatch',
                    isInteractiveEvent: false,
                    eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
                    interaction_type: interactionTypeConstants.INTERACTION_TYPE_CHECKOUT,
                    step_interaction_name:
                        stepInteractionConstants.STEP_INTERACTION_CHECKOUT_DEADEND,
                },
                null
            );
            dispatch(setTriggeredErrors(['user-mismatch']));
        }
        dispatch(setUserMismatchErrorAction({ userMismatch }));
    }, [dispatch, userMismatch]);

    useFDFQuoteVariant({
        buyer,
        item,
        deliveryOptions,
        relevantShipping: null,
        isFromCheckout: true,
    });

    const isCancelationPolicyVariant = isCheckoutCancelationPolicyVariant();

    return (
        <>
            <div className={wrapperClasses}>
                <div className={styles.stepsWrapper}>
                    {inlineSummary && (
                        <>
                            {isCancelationPolicyVariant && (
                                <CheckoutMobileCancelationPolicy item={item} />
                            )}
                            <CheckoutMobileSummaryNewPane
                                viewer={viewer}
                                item={item}
                                transaction={transaction}
                                buyer={buyer}
                                isComputing={isComputing}
                            />
                        </>
                    )}
                    <CheckoutMainError />
                    <CheckoutStepsSuspense>
                        <CheckoutSteps
                            isComputing={isComputing}
                            viewer={viewer}
                            item={item}
                            buyer={buyer}
                            transaction={transaction}
                            preCheckoutTransaction={preCheckoutTransaction}
                            deliveryOptions={deliveryOptions}
                            preloadedDuties={preloadedDuties}
                        />
                    </CheckoutStepsSuspense>
                    <CheckoutFrame item={item} />
                </div>
                {!inlineSummary && (
                    <div className={styles.sidebar}>
                        <CheckoutSidebar
                            isComputing={isComputing}
                            viewer={viewer}
                            buyer={buyer}
                            item={item}
                            transaction={transaction}
                        />
                    </div>
                )}
            </div>
            <CheckoutFooter viewer={viewer} transaction={transaction} isMobile={inlineSummary} />
            <CheckoutHelp viewer={viewer} buyer={buyer} />
        </>
    );
};
